<template>
  <Loader :value="promise" theme="overlay" class="statistics-project-widget">
    <Card
      v-if="data && wrapperComp"
      :title="title.label"
      :class="['statistics-project-widget-card', title.style ? 'statistics-project-widget-card-title' : '']"
    >
      <template v-if="!isError" #default>
        <div class="statistics-project-widget__wrapper">
          <slot name="widget" :data="data" />
        </div>
      </template>
      <template v-else #default>{{ error.message }}</template>
    </Card>
    <div v-if="!wrapperComp || source.type === 'Section'" class="statistics-project-widget__wrapper">
      <slot name="widget" :data="data" />
    </div>
  </Loader>
</template>

<script>
import Card from '@/components/Card'
import API from '@/api/Analytics'
import Hash from 'object-hash'

export default {
  props: {
    id: String,
    filter: Object,
    sources: Array,
    source: Object,
    resource: {
      type: [String, Object],
    },
  },
  watch: {
    filter: {
      handler: 'fetch',
      deep: true,
      immediate: true,
    },
  },
  data() {
    return {
      promise: null,
      data: null,
      error: null,
      divider: {
        value: 1,
        suffix: '',
      },
    }
  },
  inject: ['cache'],
  computed: {
    isError() {
      return !!this.error
    },
    wrapperComp() {
      if (!this.source) return false
      if (!('wrapper' in this.source) || this.source.wrapper === 'card' || this.source.wrapper) return true
      return false
    },
    title() {
      if (!this.source || !this.source.title) return
      return typeof this.source.title === 'function'
        ? this.source.title({ data: this.data, source: this.source })
        : this.source.title
    },
    backgroundColor() {
      return this.title && this.title.style && this.title.style.background ? this.title.style.background : '#DAE5FF'
    },
    textColor() {
      return this.title && this.title.style && this.title.style.text ? this.title.style.text : '#1d56d8'
    },
  },
  methods: {
    fetch() {
      function isCached(p, hash) {
        if (p && typeof p === 'object' && 'promise' in p && typeof p.promise.then === 'function' && p.hash === hash) {
          return true
        }

        return false
      }

      if (!this.resource) return

      let preset = typeof this.resource === 'object' ? this.resource.endpoint : this.resource
      let filter =
        this.filter && this.filter[preset]
          ? { ...this.filter[preset].filter, ...this.filter.global }
          : { ...this.resource.filter, ...this.filter.global }

      let resource =
        typeof this.resource === 'object'
          ? {
              ...this.resource,
              filter,
            }
          : this.resource

      const resourceHash = Hash(resource)

      if (isCached(this.cache[preset], resourceHash)) {
        this.promise = this.cache[preset].promise
          .then(({ data }) => {
            this.data = data
          })
          .catch(e => {
            this.data = {}
            this.error = { message: this.$t('somethingWentWrong'), data: e }
          })
        return
      }

      if (typeof resource === 'string' || typeof resource === 'object') {
        if (resource.sideeffects) {
          const sideEffectsToBust = Object.keys(this.cache).filter(key => resource.sideeffects.includes(key))
          sideEffectsToBust.forEach(source => {
            const affectedSource = this.sources.find(s => s.key === source)
            const affectedSourcePreset =
              typeof affectedSource.resource === 'object' ? affectedSource.resource.endpoint : affectedSource.resource

            const apiPromise = API.getDashboardWidget({
              id: this.id,
              preset: affectedSourcePreset,
              filter,
            })
            this.cache[affectedSourcePreset] = {
              promise: apiPromise,
              hash: Hash(affectedSourcePreset),
            }
          })
        }
        const apiPromise = API.getDashboardWidget({
          id: this.id,
          preset,
          filter,
        })
        this.cache[preset] = { promise: apiPromise, hash: resourceHash }
        this.promise = apiPromise
          .then(({ data }) => {
            this.data = data
          })
          .catch(e => {
            this.error = { message: this.$t('somethingWentWrong'), data: e }
            this.data = {}
          })
        return
      }

      throw new Error('Resource type not handled')
    },
  },
  components: {
    Card,
  },
}
</script>

<style lang="scss">
.statistics-project-widget {
  width: 100%;

  &__wrapper {
    height: 100%;
  }

  @media screen and (max-width: 1200px) {
    grid-area: auto !important;
  }

  &-card {
    height: 100%;
    position: relative;
    width: 100%;

    &-title {
      .card__title {
        background-color: v-bind(backgroundColor);
        padding: 0.4rem 0.6rem;
        max-width: max-content;
        border-radius: 4px;
        color: v-bind(textColor);
      }
    }

    &__center {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      inset: 0;
      font-weight: 900;
      font-size: 3rem;
    }

    &__chart {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 0 1rem;
      height: 100%;
      position: relative;
    }
  }
}
</style>
